// Top-level combat state machine. Pure logic, no rendering.
// Turn-based: executeHalfTurn() alternates between teams — call it once per side.
// Turn order within a round: the team with the higher-level frontliner acts first.
// Slot-based: no movement, no hex grid. Range formula:
//   spaces = (alive allies with slot < attacker.slot) + (alive enemies with slot <= target.slot)
//   can reach if spaces <= attacker.def.range

import type { HexCoord } from "../engine/hex/HexCoord";
import { EventBus } from "../engine/event/EventBus";
import type { Unit } from "../units/Unit";
import { effectiveStats } from "../units/UnitStats";
import { ITEMS } from "../data/items";
import type { CombatContext } from "./CombatContext";
import type { CombatEvents } from "./events";
import { chooseTarget } from "./AI";
import { resolveCast, resolveAutoAttack, applyHit, physicalDamage } from "./ActionResolver";
import { pickYuumiBondAlly } from "./UnitFactory";
import { getAbility } from "../units/abilities";
import type { RNG } from "../game/RNG";

export type CombatPhase = "idle" | "running" | "ended";

export class CombatEngine implements CombatContext {
  readonly bus = new EventBus<CombatEvents>();
  readonly units: Unit[];
  private phase: CombatPhase = "idle";
  private winner: "player" | "enemy" | null = null;
  private openersFired = false;
  turnNumber = 0;
  damageMult = 1;
  // Half-turn tracking: alternates first/second team each half-call.
  private onSecondHalf = false;
  private firstTeam: "player" | "enemy" = "player";
  private secondTeam: "player" | "enemy" = "enemy";
  // Step-based execution: units queued for the current half.
  private pendingUnits: Unit[] = [];
  // Unit that auto-attacked this step and still needs to cast its ability next step.
  private pendingAbilityCast: Unit | null = null;
  // Blue buff: unit that just cast and owes a second cast on the following step.
  private pendingDoubleCast: Unit | null = null;
  // Garen spin: hits held back from immediate application, released per pulse.
  private pendingSpinAttacker: Unit | null = null;
  private pendingSpinHits: import("../units/abilities/Ability").AbilityHit[] = [];

  constructor(
    players: Unit[],
    enemies: Unit[],
    private rng: RNG,
  ) {
    this.units = [...players, ...enemies];
    this.phase = "running";
  }

  // ---- Deferred spin hits (garenJudgment) ----

  deferSpinHits(attacker: Unit, hits: import("../units/abilities/Ability").AbilityHit[]): void {
    this.pendingSpinAttacker = attacker;
    this.pendingSpinHits = hits;
  }

  applySpinPulse(pulseNum: number, totalPulses: number): void {
    if (!this.pendingSpinAttacker || this.phase !== "running") return;
    const n = this.pendingSpinHits.length;
    const start = Math.round(n * (pulseNum - 1) / totalPulses);
    const end = Math.round(n * pulseNum / totalPulses);
    for (const hit of this.pendingSpinHits.slice(start, end)) {
      applyHit(this.pendingSpinAttacker, hit, this.bus);
    }
    if (pulseNum >= totalPulses) {
      this.pendingSpinAttacker = null;
      this.pendingSpinHits = [];
    }
    this.checkEnd();
  }

  // ---- CombatContext ----

  livingUnitsOf(team: "player" | "enemy"): Unit[] {
    return this.units.filter((u) => u.team === team && u.currentHp > 0);
  }

  canReach(attacker: Unit, target: Unit): boolean {
    const living = this.units.filter((u) => u.currentHp > 0);
    const enemiesUpToTarget = living.filter(
      (u) => u.team === target.team && u.pos.q <= target.pos.q,
    ).length;
    // Ranged units shoot over their own allies — only enemy depth matters.
    if (attacker.def.range > 1) {
      return enemiesUpToTarget <= attacker.def.range;
    }
    const alliesInFront = living.filter(
      (u) => u.team === attacker.team && u.pos.q < attacker.pos.q,
    ).length;
    return alliesInFront + enemiesUpToTarget <= attacker.def.range;
  }

  unitAt(coord: HexCoord): Unit | null {
    return this.units.find(
      (u) => u.currentHp > 0 && u.pos.q === coord.q && u.pos.r === coord.r,
    ) ?? null;
  }

  isBlocked(coord: HexCoord): boolean {
    return this.unitAt(coord) !== null;
  }

  inBounds(coord: HexCoord): boolean {
    return coord.q >= 0 && coord.q < 3 && (coord.r === 0 || coord.r === 1);
  }

  spawnUnit(unit: Unit): void {
    this.units.push(unit);
    this.bus.emit("unit.spawned", { unit });
  }

  teleportUnit(unit: Unit, to: HexCoord): void {
    unit.pos = to;
  }

  // ---- Public state ----
  get currentPhase(): CombatPhase { return this.phase; }
  get winningTeam(): "player" | "enemy" | null { return this.winner; }
  /** The team that will act on the next executeHalfTurn() call. */
  get actingTeam(): "player" | "enemy" {
    return this.onSecondHalf ? this.secondTeam : this.firstTeam;
  }
  /** True while a blue-buff second cast is queued. */
  get hasPendingDoubleCast(): boolean {
    return this.pendingDoubleCast !== null;
  }
  /** True while Garen spin hits are deferred (headless: call flushSpinHits to apply them). */
  get hasPendingSpinHits(): boolean { return this.pendingSpinAttacker !== null; }
  /** Apply all pending Garen spin hits immediately (used in headless simulations). */
  flushSpinHits(): void { this.applySpinPulse(1, 1); }

  // ---- Turn execution ----

  /**
   * Execute one step (auto-attack OR pending ability cast). Returns true when the
   * caller should wait a short delay before the next step; false when the half is
   * complete and the caller should wait a longer inter-team delay.
   */
  executeStep(): boolean {
    if (this.phase !== "running") return false;
    if (this.checkEnd()) return false;

    // Fire combat-start openers exactly once (no step delay for these).
    if (!this.openersFired) {
      this.openersFired = true;
      this.fireCombatStartOpeners();
      if (this.checkEnd()) return false;
    }

    // A unit auto-attacked last step and has an ability ready — cast it now.
    if (this.pendingAbilityCast) {
      const unit = this.pendingAbilityCast;
      this.pendingAbilityCast = null;
      this.executeAbilityCast(unit, false);
      if (this.checkEnd()) {
        if (this.onSecondHalf) this.decrementStatusEffects();
        this.pendingUnits = [];
        this.pendingDoubleCast = null;
        this.onSecondHalf = false;
        return false;
      }
      // Blue buff queued a second cast — give it its own step so animations don't overlap.
      if (this.pendingDoubleCast) return true;
      if (this.pendingUnits.some((u) => u.currentHp > 0)) return true;
      this.closeHalf();
      return false;
    }

    // Blue buff: fire the queued second cast as its own step.
    if (this.pendingDoubleCast) {
      const unit = this.pendingDoubleCast;
      this.pendingDoubleCast = null;
      this.executeAbilityCast(unit, true);
      if (this.checkEnd()) {
        if (this.onSecondHalf) this.decrementStatusEffects();
        this.pendingUnits = [];
        this.onSecondHalf = false;
        return false;
      }
      if (this.pendingUnits.some((u) => u.currentHp > 0)) return true;
      this.closeHalf();
      return false;
    }

    // Load the next half's unit queue when empty.
    if (this.pendingUnits.length === 0) {
      if (!this.onSecondHalf) {
        this.turnNumber++;
        [this.firstTeam, this.secondTeam] = this.determineTurnOrder();
        this.bus.emit("combat.turnStarted", { turnNumber: this.turnNumber });
        this.pendingUnits = this.livingUnitsOf(this.firstTeam).sort((a, b) => a.pos.q - b.pos.q);
      } else {
        this.pendingUnits = this.livingUnitsOf(this.secondTeam).sort((a, b) => a.pos.q - b.pos.q);
      }
    }

    // Process units — skip dead ones inline, stop after one acts.
    while (this.pendingUnits.length > 0) {
      const unit = this.pendingUnits.shift()!;
      if (unit.currentHp <= 0) continue;
      this.executeUnitAutoAttack(unit);
      if (this.checkEnd()) {
        if (this.onSecondHalf) this.decrementStatusEffects();
        this.pendingUnits = [];
        this.pendingAbilityCast = null;
        this.onSecondHalf = false;
        return false;
      }
      // If this unit has a pending ability cast, signal a short delay before it fires.
      if (this.pendingAbilityCast) return true;
      if (this.pendingUnits.some((u) => u.currentHp > 0)) return true;
      break;
    }

    this.closeHalf();
    return false;
  }

  private closeHalf(): void {
    if (!this.onSecondHalf) {
      this.onSecondHalf = true;
    } else {
      this.decrementStatusEffects();
      this.bus.emit("combat.turnEnded", { turnNumber: this.turnNumber });
      this.onSecondHalf = false;
    }
  }

  private determineTurnOrder(): ["player" | "enemy", "player" | "enemy"] {
    const playerFront = this.livingUnitsOf("player").sort((a, b) => a.pos.q - b.pos.q)[0];
    const enemyFront = this.livingUnitsOf("enemy").sort((a, b) => a.pos.q - b.pos.q)[0];
    const playerLevel = playerFront?.level ?? 0;
    const enemyLevel = enemyFront?.level ?? 0;
    // Player wins ties
    return playerLevel >= enemyLevel ? ["player", "enemy"] : ["enemy", "player"];
  }

  // Phase 1 of a unit's action: auto-attack and charge the ability.
  // If the ability becomes ready, sets pendingAbilityCast so the cast fires next step.
  private executeUnitAutoAttack(unit: Unit): void {
    if (unit.stasisTurnsRemaining > 0) return;
    if (unit.stunTurnsRemaining > 0) return;
    if (unit.sleepTurnsRemaining > 0) return;

    if (unit.tibbersUnitId !== null) {
      const tibbers = this.units.find((u) => u.id === unit.tibbersUnitId);
      if (!tibbers || tibbers.currentHp <= 0) unit.tibbersUnitId = null;
    }

    if (!unit.currentTarget || unit.currentTarget.currentHp <= 0 || !this.canReach(unit, unit.currentTarget)) {
      unit.currentTarget = chooseTarget(unit, this, this.rng);
    }
    const target = unit.currentTarget;
    const blocked = !target || !this.canReach(unit, target);

    if (blocked) {
      // All units passively charge when they can't reach — no attack animation.
      if (unit.def.abilityId && unit.cooldownTurns > 0) unit.cooldownTurns--;
      if (unit.def.abilityId && unit.cooldownTurns === 0 && unit.tibbersUnitId === null) {
        this.pendingAbilityCast = unit;
      }
      return;
    }


    if (unit.def.baseAttackSpeed > 0) {
      resolveAutoAttack(unit, target!, this.bus, this.damageMult);
    }

    // Jayce: transform to hammer form when he becomes the frontmost living ally
    if (unit.championDef?.id === "Jayce" && !unit.jayceHammerForm) {
      const alliesInFront = this.livingUnitsOf(unit.team).filter(u => u.pos.q < unit.pos.q).length;
      if (alliesInFront === 0) {
        unit.jayceHammerForm = true;
        unit.def.range = 1;
      }
    }

    // Auto-attack charges the ability.
    if (unit.cooldownTurns > 0) unit.cooldownTurns--;

    if (unit.def.abilityId && unit.cooldownTurns === 0 && unit.tibbersUnitId === null) {
      this.pendingAbilityCast = unit;
    }
  }

  // Phase 2 of a unit's action: cast the ability (fires after a short delay).
  // isDoubleCast=true is the blue-buff follow-up cast: skip the cooldown reset and
  // mark the event so the log/animator can distinguish the two casts.
  private executeAbilityCast(unit: Unit, isDoubleCast: boolean): void {
    if (unit.currentHp <= 0 || !unit.def.abilityId) return;
    let castTarget: Unit | null;
    if (unit.championDef?.yuumiAura) {
      // Yuumi targets the ally she's bonded to — no enemy reachability required.
      const allies = this.livingUnitsOf(unit.team);
      castTarget = allies.find(u => u.id !== unit.id && u.yuumiBoostMult > 1) ?? pickYuumiBondAlly(unit, allies);
    } else {
      castTarget = unit.currentTarget;
      if (!castTarget || castTarget.currentHp <= 0 || !this.canReach(unit, castTarget)) {
        unit.currentTarget = chooseTarget(unit, this, this.rng);
        castTarget = unit.currentTarget;
      }
    }
    if (castTarget && castTarget.currentHp > 0) {
      resolveCast(unit, castTarget, this, this.bus, this.rng, isDoubleCast);
      if (!isDoubleCast) unit.cooldownTurns = unit.def.abilityCooldown;
      unit.abilityCastCount = (unit.abilityCastCount ?? 0) + 1;

      // Blue buff: queue a second cast as its own step so it animates after the first.
      if (!isDoubleCast && unit.blueBuff && unit.currentHp > 0) {
        this.pendingDoubleCast = unit;
      }
    }
  }

  private decrementStatusEffects(): void {
    for (const unit of this.units) {
      if (unit.stunTurnsRemaining > 0) unit.stunTurnsRemaining--;
      if (unit.stasisTurnsRemaining > 0) unit.stasisTurnsRemaining--;
      if (unit.sleepTurnsRemaining > 0) unit.sleepTurnsRemaining--;

      // Bleed DoT tick
      if (unit.bleedTurnsRemaining > 0 && unit.currentHp > 0) {
        const bleedDmg = Math.round(unit.bleedTickDamage * this.damageMult);
        unit.currentHp = Math.max(0, unit.currentHp - bleedDmg);
        this.bus.emit("damage.dealt", { source: unit, target: unit, amount: bleedDmg, type: "physical", remainingHp: unit.currentHp, suppressPopup: false });
        if (unit.currentHp <= 0) this.bus.emit("unit.died", { unit });
        unit.bleedTurnsRemaining--;
        if (unit.bleedTurnsRemaining === 0) unit.bleedTickDamage = 0;
      }

      if (unit.damageReductionTurnsRemaining > 0) {
        unit.damageReductionTurnsRemaining--;
        if (unit.damageReductionTurnsRemaining === 0) unit.damageReductionPercent = 0;
      }

      if (unit.olafUltTurnsRemaining > 0) unit.olafUltTurnsRemaining--;
      if (unit.alphaStrikeTurnsRemaining > 0) unit.alphaStrikeTurnsRemaining--;

      if (unit.jaxCounterStrikeTurnsRemaining > 0) {
        unit.jaxCounterStrikeTurnsRemaining--;
        if (unit.jaxCounterStrikeTurnsRemaining === 0) {
          const stats = effectiveStats(unit);
          const blocked = unit.jaxCounterStrikeBlockedCount;
          const slamDmg = physicalDamage((stats.ad * 0.65 + 55) * (1 + 0.20 * blocked));
          unit.jaxCounterStrikeBlockedCount = 0;
          const enemyTeam: "player" | "enemy" = unit.team === "player" ? "enemy" : "player";
          const closest = this.livingUnitsOf(enemyTeam)
            .sort((a, b) => a.pos.q - b.pos.q)
            .slice(0, 2);
          for (const enemy of closest) {
            applyHit(unit, { target: enemy, amount: slamDmg, type: "physical", jaxSlam: true }, this.bus, this.damageMult);
          }
          this.bus.emit("jax.slammed", { unit });
        }
      }

      if (unit.healReductionTurnsRemaining > 0) {
        unit.healReductionTurnsRemaining--;
        if (unit.healReductionTurnsRemaining === 0) unit.healReductionPercent = 0;
      }

      // Warmog's Armor: heal 5% max HP per turn
      if (unit.item === "warmogsArmor" && unit.currentHp > 0) {
        const maxHp = effectiveStats(unit).maxHp;
        if (unit.currentHp < maxHp) {
          const heal = Math.round(maxHp * 0.05 * (1 - unit.healReductionPercent));
          if (heal > 0) {
            unit.currentHp = Math.min(maxHp, unit.currentHp + heal);
            this.bus.emit("unit.healed", { unit, amount: heal });
          }
        }
      }
    }
  }

  private fireCombatStartOpeners(): void {
    // Locket of the Iron Solari: shield holder and adjacent-slot allies at combat start.
    for (const unit of this.units) {
      if (unit.item !== "locket" || unit.currentHp <= 0) continue;
      const shieldPct = ITEMS.locket.locketShieldPct!;
      const holderMaxHp = effectiveStats(unit).maxHp;
      const shieldAmt = Math.round(holderMaxHp * shieldPct);
      unit.shieldAmount += shieldAmt;
      this.bus.emit("unit.statusApplied", { unit, effect: { kind: "shield", amount: shieldAmt } });
      for (const ally of this.units) {
        if (ally.team !== unit.team || ally.currentHp <= 0 || ally === unit) continue;
        if (Math.abs(ally.pos.q - unit.pos.q) <= 1) {
          ally.shieldAmount += shieldAmt;
          this.bus.emit("unit.statusApplied", { unit: ally, effect: { kind: "shield", amount: shieldAmt } });
        }
      }
    }

  }

  private checkEnd(): boolean {
    const playersAlive = this.livingUnitsOf("player").length > 0;
    const enemiesAlive = this.livingUnitsOf("enemy").length > 0;
    if (!playersAlive || !enemiesAlive) {
      this.winner = playersAlive ? "player" : "enemy";
      this.endCombat();
      return true;
    }
    return false;
  }

  private endCombat(): void {
    if (this.phase === "ended") return;
    this.phase = "ended";
    if (!this.winner) {
      this.winner = this.livingUnitsOf("player").length > 0 ? "player" : "enemy";
    }
    this.bus.emit("combat.ended", { winner: this.winner });
  }
}
